import {
  Component,
  Input,
  EventEmitter,
  Output,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy
} from '@angular/core';
import { SmartSearchMode, SmartQueryInput } from '../common.data.types';
import { FreeFormFilterComponent } from '../free.form.filter/free.form.filter.component';
import { ExpertModeComponent } from '../expert.mode/expert.mode.component';
import { TimeoutWrapperService } from '@nrwl/common-lib';
import { get } from 'lodash';
import {
  ChipExpression,
  QueryExpressionParserService
} from '@nrwl/query-emitter';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { NavigationProvider } from '@nrwl/navigation-abstract';

export interface ISmartSearchLayoutPreferences {
  hideFilterLabel?: boolean;
  hideHelpIcon?: boolean;
  noBorder?: boolean;
  hideModeSwitch?: boolean;
  hideValidStatus?: boolean;
  placeholder?: string;
  supportedSearchKeywords?: Array<string>;
  showClearAll: Subject<boolean>;
  autoSuggest?: {
    enable?: boolean;
    forTerm?: boolean;
  };
}

@Component({
  selector: 'nrwl-smart-search',
  templateUrl: './smart.search.component.html',
  styleUrls: ['./smart.search.component.scss'],
  providers: [TimeoutWrapperService],
  encapsulation: ViewEncapsulation.None
})
export class SmartSearchComponent implements OnChanges, OnDestroy {
  @Input() mode: SmartSearchMode;
  @Input() treatEmptyAsValid: boolean;
  @Input() readonlyChipState: boolean;

  isClearAllVisibile = false;
  // a way to pass into deep nested components my preferences on layout (eg, turn off mode switch,
  // hide borders, etc.)
  @Input() layoutPreferences: ISmartSearchLayoutPreferences;
  @Input() predefinedExpression: {
    expression: ChipExpression[];
    triggerSearch: boolean;
  };
  @Input() isAnalytics: boolean;
  @Output() inputChanged = new EventEmitter<SmartQueryInput>();
  @Output() inputFieldChangeEvent?= new EventEmitter<any>();
  queryValidStatus = 'checked';

  smartSearchMode: typeof SmartSearchMode = SmartSearchMode;
  // internal property, used to exchange query expression str between edit modes
  queryExpression = '';
  switchingAllowed = true;

  @ViewChild(FreeFormFilterComponent) basicChild: FreeFormFilterComponent;
  @ViewChild(ExpertModeComponent) expertChild: ExpertModeComponent;

  constructor(
    readonly queryBuilder: QueryExpressionParserService,
    private readonly timeout: TimeoutWrapperService,
    private readonly navigator: NavigationProvider,
    private readonly router: Router
  ) { }

  switchMode() {
    this.mode =
      this.mode === SmartSearchMode.SIMPLE
        ? SmartSearchMode.EXPERT
        : SmartSearchMode.SIMPLE;
  }

  clearAll() {
    if (this.basicChild) {
      this.basicChild.clearAll();
    } else if (this.expertChild) {
      this.expertChild.updateQuery('');
    }
  }

  inputChangedListener(event: SmartQueryInput) {
    this.queryExpression = event.rawQuery;
    this.switchingAllowed = event.valid;
    this.queryValidStatus = event.valid ? 'checked' : 'error';
    this.isClearAllVisibile = this.queryExpression !== '';
    this.inputChanged.emit(event); // emit it further
  }

  inputFieldChangeListener(inputData) {
    this.inputFieldChangeEvent.emit(inputData);
  }

  ngOnDestroy() {
    this.timeout.onDestroy();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Set predefinedExpression filter on firstChange only if expression is filled
    const change =
      (!get(changes.predefinedExpression, 'firstChange', true) &&
        get(changes.predefinedExpression, 'currentValue') !== undefined) ||
      get(changes.predefinedExpression, 'currentValue.expression.length', 0) >
      0;

    if (change) {
      const predefinedExpression = get(
        changes.predefinedExpression,
        'currentValue',
        {}
      );
      const prevExpression = this.queryBuilder.toExpression(
        get(changes.predefinedExpression, 'previousValue', {}).expression
      );
      this.queryExpression = this.queryBuilder.toExpression(
        predefinedExpression.expression
      );
      // stick to chip based mode.
      // This might become incorrect in case we start constructing complex expressions with OR, XOR and so on
      this.mode = SmartSearchMode.SIMPLE;

      this.timeout.set(
        'onChanges',
        () => {
          if (this.basicChild) {
            this.basicChild.readonlyChipState = this.readonlyChipState;
            this.basicChild.attemptAcceptExpression(
              predefinedExpression.triggerSearch
            );
          }
        },
        100
      );

      if (
        !this.queryExpression &&
        prevExpression &&
        this.router.routerState.snapshot.url.indexOf('lndgpg') > -1
      ) {
        this.navigator.navigateToPage('yuidsa');
      }
    }
    // layoutPreferences might also change. But we pass that info to our child components/nothing needs to be done here.
  }
}
